// Inbox settings (G-82, changed by Tim 2026-10-24): one section per inbox tab, a checkbox per notice
// type and one for the whole section. Account-wide; an unticked type is not delivered at all.
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_SETTING_TYPES,
  SWITCHABLE_NOTIFICATION_TYPES,
} from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { endpoints } from '../api/endpoints';
import { queryKeys, useNotificationSettings } from '../api/queries';
import { NOTIFICATION_TAB_LABELS, NOTIFICATION_TYPE_LABELS } from '../labels';
import { Checkbox, ErrorNotice, Sheet, Spinner } from './ui';

export function InboxSettingsSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const queryClient = useQueryClient();
  const settings = useNotificationSettings();
  const save = useMutation({
    mutationFn: endpoints.putNotificationSettings,
    // Saves run one after another; the cache is updated at once, so quick clicks build on each other.
    scope: { id: 'notification-settings' },
    onMutate: (next) => queryClient.setQueryData(queryKeys.notificationSettings, next),
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.notificationSettings }),
  });
  const disabled = new Set<string>(settings.data?.disabledTypes ?? []);
  const update = (types: readonly string[], on: boolean) => {
    const next = new Set(disabled);
    for (const type of types) {
      if (on) next.delete(type);
      else next.add(type);
    }
    save.mutate({ disabledTypes: SWITCHABLE_NOTIFICATION_TYPES.filter((t) => next.has(t)) });
  };

  return (
    <Sheet open={open} title="Inbox settings" onClose={onClose}>
      <p className="text-xs text-muted">
        Unticked notices are not delivered, in every galaxy. Notices already in the inbox stay.
      </p>
      {settings.error ? <ErrorNotice error={settings.error} /> : null}
      {save.error ? <ErrorNotice error={save.error} /> : null}
      {settings.data ? (
        <div className="mt-2 space-y-2">
          {NOTIFICATION_CATEGORIES.map((category) => {
            const types = NOTIFICATION_SETTING_TYPES[category];
            const on = types.filter((t) => !disabled.has(t)).length;
            return (
              <section
                key={category}
                data-settings-section={category}
                className="border-t border-line pt-1"
              >
                <Checkbox
                  label={
                    <span className="font-display font-bold uppercase tracking-[0.08em]">
                      All {NOTIFICATION_TAB_LABELS[category]} notices
                    </span>
                  }
                  checked={on === types.length}
                  indeterminate={on > 0 && on < types.length}
                  onChange={(e) => update(types, e.target.checked)}
                />
                <div className="pl-8">
                  {types.map((type) => (
                    <Checkbox
                      key={type}
                      label={NOTIFICATION_TYPE_LABELS[type] ?? type}
                      checked={!disabled.has(type)}
                      onChange={(e) => update([type], e.target.checked)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <Spinner />
      )}
    </Sheet>
  );
}
